/**
 * RYZE HEADER (W2, 2026-10-18): SVNTY's header behaviour in RYZE's files.
 * ============================================================
 * 1. THE HEADSTACK: the announcement bar leaves after 6 px of travel down
 *    and returns after 6 px up (SVNTY's svnty-headstack.js: hysteresis
 *    anchored on the direction flip, beats 0.14 s out / 0.18 s back scaled
 *    by the distance left, instant inside the top zone). The bar under it
 *    never moves. While a door is open (drawer, panel, search) scrolling
 *    never moves the bar. Publishes html.ryze-bar-hidden + html.ryze-at-top.
 * 2. THE LOCK: SVNTY's iOS-safe scroll lock: one class on <html>, the
 *    scrollbar gutter compensated, touchmove cancelled outside
 *    [data-ryze-scroll] scrollers (and at their ends), the saved scroll
 *    position restored on release. Never `overflow` on <html>.
 * 3. THE DRAWER (< 750; < 990 before dev2.13-band): burger toggle, one close path, Escape returns
 *    focus to the burger, Tab is trapped inside burger + drawer, links
 *    close it. The accordions (.ryze-mm__toggle) live here too.
 * 4. THE PANEL (750+; 990+ before dev2.13-band): hover intent 90 ms (mouse
 *    only), 160 ms grace on leave, THE SAFE TRIANGLE on a leave toward the
 *    panel (dev2.15-fix-chrome), a mouse click only opens (never shuts the
 *    panel its hover opened), Enter/Space and a tap toggle,
 *    ArrowDown opens and focuses the first link, Escape closes and returns
 *    focus, focus leaving closes, a press outside closes, 8 px of scroll
 *    closes. From 750 the panel is fixed and seated by THE TAB RAIL
 *    (assets/ryze-nav-rail.js: window.RYZE.navRail.show brings its tab clear
 *    of a fade and places the panel under it); the rail shuts it through
 *    window.RYZE.navPanel.close when its tab rests under a fade.
 * 5. THE SEARCH: one dialog. /search/suggest with section_id
 *    ryze-predictive-search (250 ms debounce, 2 characters, aborted and
 *    cached), ArrowUp/Down over the options with aria-activedescendant,
 *    Enter follows the selected option, recent searches in localStorage
 *    (ryze:search:recent, 6), Tab trapped, Escape and the scrim close.
 * 6. THE ROLLER: the announcement bar, one message at a time (hold 4000,
 *    roll 600), held on hover / touch / focus / hidden tab / pause button,
 *    swipe 24 px or ArrowLeft/Right to step; reduced motion = instant swap
 *    (CSS). The live region stays off until the shopper steps it.
 * 7. THE CART LABEL: the cart link's aria-label follows the badge.
 * ============================================================
 */
(function () {
  'use strict';
  /* CODE-26 (2026-10-18): one run per page, whatever loads it twice. */
  if (window.__ryzeHeaderInit) return;
  window.__ryzeHeaderInit = true;
  var root = document.documentElement;
  var bar = document.querySelector('.ryze-announce');
  var nav = document.querySelector('.ryze-nav');
  if (!nav) return;
  var COARSE = window.matchMedia('(pointer: coarse)').matches;
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  /* dev2.13-band (2026-10-30): the desktop row, the panel and the end of the drawer begin at 750 (were 990), except on
     an iPhone on its side (THE ROTATED-PHONE GATE, SVNTY's D5-2: it keeps the phone bar and the drawer); the query is
     the list sections/ryze-header.liquid writes on the rail (data-ryze-desk-mq), the same one as ryze-header.css */
  var deskEl = nav.querySelector('[data-ryze-desk-mq]');
  var deskMQ = window.matchMedia((deskEl && deskEl.getAttribute('data-ryze-desk-mq')) || '(min-width: 750px)');
  /* CODE-26: MediaQueryList change listener with the Safari < 14 fallback. */
  function onMQ(mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }

  /* ── 1. THE HEADSTACK ─────────────────────────────────────── */
  var THRESHOLD = 6;
  var barH = 0, maxY = 0, lastY = 0, refY = 0, dir = 0, hidden = false, doors = 0;
  function measure() {
    barH = bar ? bar.offsetHeight : 0;
    maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }
  /* THE STATUS BAR (iOS 15-18 read the meta; 26/27 sample .ryze-strip, CSS): gold while the bar shows, paper when it is away */
  var statusMeta = document.querySelector('meta[name="theme-color"]');
  var BAR_FLAT = (getComputedStyle(root).getPropertyValue('--ryze-bar-flat') || '#d6c1a0').trim() || '#d6c1a0';
  var PAPER = (getComputedStyle(root).getPropertyValue('--ryze-paper') || '#ffffff').trim() || '#ffffff';   /* THE WHITE PASS: the page paper */
  var SCRIM_GOLD = (getComputedStyle(root).getPropertyValue('--ryze-strip-scrim-gold') || '#82755f').trim() || '#82755f';
  var SCRIM_PAPER = (getComputedStyle(root).getPropertyValue('--ryze-strip-scrim-paper') || '#999794').trim() || '#999794';
  /* THE KNOCK (SVNTY's headstack): iOS samples the page top at scroll end and after a layout it notices; a popup that opens
     with no scroll (the promo greeting, a modal) leaves the old tone frozen under the status bar (the owner, 30 Oct 01:40
     Cairo: the gold stayed over the dimmed page). Stepping the strip's z-index for two frames, 700 ms and 1600 ms after a
     door settles, makes Safari re-sample. */
  var strip = document.querySelector('.ryze-strip');
  function knock() {
    if (!strip) return;
    strip.classList.add('is-knock');
    requestAnimationFrame(function () { requestAnimationFrame(function () { strip.classList.remove('is-knock'); }); });
  }
  function knocks() { setTimeout(knock, 700); setTimeout(knock, 1600); }
  function paintStatusBar() {
    if (!statusMeta) return;
    var away = hidden || doors > 0;
    var scrim = document.body.classList.contains('overflow-hidden');   /* a dark-scrim door (promo, cart drawer, modals) */
    statusMeta.setAttribute('content', scrim ? (away ? SCRIM_PAPER : SCRIM_GOLD) : (away ? PAPER : BAR_FLAT));
  }
  var scrimWas = false;
  if ('MutationObserver' in window) new MutationObserver(function () {
    paintStatusBar();
    var scrimNow = document.body.classList.contains('overflow-hidden');
    if (scrimNow !== scrimWas) { scrimWas = scrimNow; knocks(); }
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  function setHidden(next, force) {
    if (next === hidden || !barH) return;
    if (!force && doors > 0) return;
    hidden = next;
    var beat = reduceMQ.matches ? 0 : (next ? 0.14 : 0.18);
    root.style.setProperty('--ryze-slab-beat', beat + 's');
    root.classList.toggle('ryze-bar-hidden', hidden);
    paintStatusBar();
  }
  function onScroll() {
    var y = window.scrollY;
    if (y < 0 || y > maxY + 1) { lastY = y; return; }
    var atTop = y <= barH;
    root.classList.toggle('ryze-at-top', atTop);
    nav.classList.toggle('scrolled', y > 60);
    if (atTop) { setHidden(false); dir = 0; refY = lastY = y; return; }
    var d = y > lastY ? 1 : (y < lastY ? -1 : 0);
    if (d && d !== dir) { dir = d; refY = lastY; }
    if (dir === 1 && y - refY > THRESHOLD) setHidden(true);
    if (dir === -1 && refY - y > THRESHOLD) setHidden(false);
    lastY = y;
  }
  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; onScroll(); });
  }, { passive: true });
  window.addEventListener('resize', function () { measure(); onScroll(); }, { passive: true });
  window.addEventListener('load', measure);
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(document.body);
  measure();
  lastY = refY = window.scrollY;
  root.classList.toggle('ryze-at-top', window.scrollY <= barH);
  nav.classList.toggle('scrolled', window.scrollY > 60);
  /* keyboard focus entering the stack brings the bar home */
  [bar, nav].forEach(function (el) {
    if (el) el.addEventListener('focusin', function () { setHidden(false, true); });
  });
  function claim() { doors++; }
  function release() { setTimeout(function () { doors = Math.max(0, doors - 1); if (!locked) paintStatusBar(); }, 160); }
  paintStatusBar();

  /* ── 2. THE LOCK ──────────────────────────────────────────── */
  var locked = 0, savedY = 0, touchY = 0, touchX = 0, touchScroller = null, touchRail = false;
  function scrollerOf(t) { return t && t.closest ? t.closest('[data-ryze-scroll]') : null; }
  function onTouchStart(e) {
    touchY = e.touches[0].clientY; touchX = e.touches[0].clientX; touchScroller = scrollerOf(e.target);
    touchRail = !!(e.target && e.target.closest && e.target.closest('[data-ryze-pan-x]'));
  }
  function onTouchMove(e) {
    var s = touchScroller;
    if (!s) { e.preventDefault(); return; }
    var dy = e.touches[0].clientY - touchY;
    /* dev2.15-search: a sideways swipe on a rail that pans sideways ([data-ryze-pan-x]: the sheet's chip rails and the
       tailored rail) is the rail's own pan, never cut at the scroller's vertical ends; anywhere else the old rule stands */
    if (touchRail && Math.abs(e.touches[0].clientX - touchX) > Math.abs(dy)) return;
    var atStart = s.scrollTop <= 0, atEnd = s.scrollTop + s.clientHeight >= s.scrollHeight - 1;
    if ((dy > 0 && atStart) || (dy < 0 && atEnd)) e.preventDefault();
  }
  /* THE CURTAIN (dev2.13-drawer): one node for the page's life, shown only under html.ryze-parked (ryze-header.css),
     placed at the held scroll spot so the header's frost samples paper while a door is open */
  var curtain = document.createElement('div');
  curtain.className = 'ryze-curtain';
  curtain.setAttribute('aria-hidden', 'true');
  document.body.appendChild(curtain);
  var beatT = 0;
  function lock() {
    if (locked++) return;
    savedY = window.scrollY;
    root.style.setProperty('--ryze-lock-gutter', Math.max(0, window.innerWidth - root.clientWidth) + 'px');
    clearTimeout(beatT);
    root.classList.remove('ryze-door-beat');
    /* THE ONE LOCK: the hold and the park land in one write (SVNTY D6-17): one style pass, one layout, one frame.
       THE PARK IS FOR TOUCH (SVNTY D8-9, the owner: "on the desktop keep the bar as is"): a mouse keeps the bar where it
       stands; the curtain is placed only when the bar parks (the refuter's P1-1). */
    if (COARSE) { curtain.style.top = savedY + 'px'; root.classList.add('ryze-locked', 'ryze-parked'); }
    else root.classList.add('ryze-locked');
    claim();
    paintStatusBar();
    knocks();
    if (COARSE) {
      document.addEventListener('touchstart', onTouchStart, { passive: true });
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      requestAnimationFrame(function () { requestAnimationFrame(function () { if (locked) root.classList.add('ryze-held'); }); });
    }
  }
  function unlock() {
    if (!locked || --locked) return;
    /* THE RETURN: parked becomes door-beat in one write; the slab slides back on 0.18 s where the page wants the bar */
    var wasParked = root.classList.contains('ryze-parked');
    root.classList.remove('ryze-locked', 'ryze-held', 'ryze-parked');
    if (wasParked) {
      root.classList.add('ryze-door-beat');
      clearTimeout(beatT);
      beatT = setTimeout(function () { root.classList.remove('ryze-door-beat'); }, 220);
    }
    release();
    knocks();
    if (COARSE) {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    }
    if (Math.abs(window.scrollY - savedY) > 2) window.scrollTo(0, savedY);
  }
  window.RYZE = window.RYZE || {};
  window.RYZE.headLock = { lock: lock, unlock: unlock };

  function focusables(scope) {
    return Array.prototype.filter.call(
      scope.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
  }
  function trapTab(e, scopes) {
    if (e.key !== 'Tab') return;
    var list = [];
    scopes.forEach(function (s) { list = list.concat(focusables(s)); });
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ── 3. THE DRAWER ────────────────────────────────────────── */
  var burger = nav.querySelector('.ryze-nav__hamburger');
  var drawer = document.getElementById('ryze-mobile-menu');
  /* THE ART WAKES ON INTENT (W1 2026-10-18, site audit #10; carried into
     this file by W-LAUNCH 2026-10-18 when W2 moved the drawer here): the
     offer stills in the drawer tiles and the desktop panel ship data-src
     (sections/ryze-header.liquid), so a visitor who never opens either
     never downloads them. Idempotent: a woken img has no data-src left. */
  function wakeArt(root) {
    if (!root) return;
    root.querySelectorAll('.ryze-mm__tile img[data-src], .ryze-panel__offer img[data-src]').forEach(function (img) {
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
    });
  }
  function drawerOpen() { return drawer && drawer.classList.contains('is-open'); }
  var closeSearchRef = null;   /* set by the search block below: the drawer closes an open sheet first (the doors hand off) */
  function openDrawer() {
    if (!drawer || drawerOpen()) return;
    if (closeSearchRef) closeSearchRef();
    wakeArt(drawer);
    drawer.classList.add('is-open');
    burger.classList.add('is-active');
    burger.setAttribute('aria-expanded', 'true');
    lock();
  }
  function focusDrawerFirst() {
    if (!drawerOpen()) return;
    drawer.removeAttribute('inert');   /* the observer's microtask would come too late for this task's focus */
    var first = focusables(drawer)[0] || drawer.querySelector('a[href], button');
    if (first) first.focus({ preventScroll: true });
  }
  function closeDrawer(returnFocus) {
    if (!drawerOpen()) return;
    drawer.classList.remove('is-open');
    burger.classList.remove('is-active');
    burger.setAttribute('aria-expanded', 'false');
    unlock();
    if (returnFocus && !COARSE) burger.focus();   /* THE FOCUS LAW: no ring after a tap */
  }
  if (burger && drawer) {
    drawer.setAttribute('inert', '');
    ['pointerdown', 'mouseenter', 'focus'].forEach(function (ev) {
      burger.addEventListener(ev, function () { wakeArt(drawer); }, { once: true, passive: true });
    });
    var syncInert = function () { if (drawerOpen()) drawer.removeAttribute('inert'); else drawer.setAttribute('inert', ''); };
    new MutationObserver(syncInert).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    burger.addEventListener('click', function () { drawerOpen() ? closeDrawer(false) : openDrawer(); });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a[href]')) closeDrawer(false);
    });
    document.addEventListener('keydown', function (e) {
      if (!drawerOpen()) return;
      if (e.key === 'Escape') { closeDrawer(true); return; }
      trapTab(e, [burger, drawer]);
    });
    onMQ(deskMQ, function (e) { if (e.matches) closeDrawer(false); });
  }
  document.querySelectorAll('.ryze-mm__toggle').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', String(open));
      var sub = document.getElementById(btn.getAttribute('aria-controls')) || btn.nextElementSibling;
      if (sub) sub.classList.toggle('is-open', open);
    });
  });

  /* ── 4. THE PANEL ─────────────────────────────────────────── */
  var panelClosers = [], panelOthers = [];
  window.RYZE.navPanel = { close: function () { panelClosers.forEach(function (f) { f(); }); } };
  /* THE SAFE TRIANGLE (dev2.15-fix-chrome, 30 Oct 2026; the intent of SVNTY's svnty-nav-rail.js THE HEADER PANELS v3, after
     Floating UI's safePolygon, MIT): the panel hangs from the bar's foot and is wider than its tab, so a mouse on its way
     from the tab to the far side of the panel leaves the item through the bar (over a neighbour tab) before it reaches the
     panel's top edge; the 160 ms grace shut the panel under a slow diagonal path (measured: 800 ms from FRAGRANCES to the
     panel's far corner closed it at 1440 and 960, Chromium and WebKit). A leave ABOVE the panel's top edge now draws the
     triangle from the leave point to the panel's two top corners: while the pointer moves inside it the panel stays; a rest
     there of AIM_REST ms, or a step out of it, falls back to the old grace. A leave through the panel's own sides or foot
     keeps the 160 ms grace as before. Mouse only; touch and keys never reach it. The tip sits AIM_TIP px behind the leave
     point and the samples are pointermove's (fractional, as the leave's are): Chromium's mousemove reports whole pixels, and
     the first sample after a leave at 420.77 read 420, a hair outside a tip drawn on the leave point itself. */
  var AIM_REST = 300, AIM_TIP = 4;
  function inTri(p, a, b, c) {   /* the point is on the same side of all three edges */
    function side(u, v, w) { return (u[0] - w[0]) * (v[1] - w[1]) - (v[0] - w[0]) * (u[1] - w[1]); }
    var d1 = side(p, a, b), d2 = side(p, b, c), d3 = side(p, c, a);
    return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
  }
  nav.querySelectorAll('[data-ryze-panel-item]').forEach(function (item) {
    var tab = item.querySelector('.ryze-nav__tab');
    var panel = item.querySelector('.ryze-panel');
    if (!tab || !panel) return;
    var openT = 0, closeT = 0, openY = 0, lastTouch = 0, isOpen = false, downType = '', aim = null;
    panel.setAttribute('inert', '');
    function stopAim() {
      if (!aim) return;
      document.removeEventListener('pointermove', aim.fn, true);
      clearTimeout(aim.restT);
      aim = null;
    }
    function grace() { clearTimeout(closeT); closeT = setTimeout(function () { close(false); }, 160); }
    function startAim(x, y) {
      stopAim();
      var R = panel.getBoundingClientRect();
      if (!R.width || !R.height || y >= R.top - 0.5) { grace(); return; }   /* out through the panel's sides or foot: the old grace */
      var tri = [[x, y - AIM_TIP], [R.left - 1, R.top + 1], [R.right + 1, R.top + 1]];
      var a = { restT: 0 };
      function rest() { clearTimeout(a.restT); a.restT = setTimeout(function () { stopAim(); close(false); }, AIM_REST); }
      a.fn = function (ev) {
        if (ev.pointerType && ev.pointerType !== 'mouse') return;
        if (inTri([ev.clientX, ev.clientY], tri[0], tri[1], tri[2])) { clearTimeout(closeT); rest(); return; }
        stopAim(); grace();                                                    /* out of the triangle: the old grace */
      };
      aim = a;
      document.addEventListener('pointermove', a.fn, true);
      rest();
    }
    function open() {
      clearTimeout(closeT);
      stopAim();
      if (isOpen) return;
      /* ONE PANEL AT A TIME (the refuter's P2-2): at 820 HELP's tab sits inside FRAGRANCES' triangle, and a path that met it
         opened HELP while FRAGRANCES was still held (both open 200-250 ms); the panel that opens shuts every other at once */
      panelOthers.forEach(function (o) { if (o.item !== item) o.close(); });
      isOpen = true;
      wakeArt(panel);
      if (window.RYZE.navRail) window.RYZE.navRail.show(item);   /* THE TAB RAIL: the tab clear of a fade, the panel seated under it */
      else if (deskMQ.matches) {
        /* the rail's script not here (slow, blocked, failed): seat the fixed panel by hand where the absolute one hung, the
           tab's start less 24 and the bar's bottom, in the bar's coordinates (.ryze-nav's transform is the panel's
           containing block; the bar has no left or top border) */
        var nr = nav.getBoundingClientRect(), ir = item.getBoundingClientRect();
        var px = Math.max(16, Math.min(ir.left - 24, document.documentElement.clientWidth - panel.offsetWidth - 16));   /* 16 px inside the viewport, as the rail seats it */
        panel.style.setProperty('--ryze-panel-left', (px - nr.left) + 'px');
        panel.style.setProperty('--ryze-panel-top', (ir.bottom - nr.top) + 'px');
      }
      item.classList.add('is-open');
      tab.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('inert');
      openY = window.scrollY;
      claim();
    }
    function close(returnFocus) {
      clearTimeout(openT);
      clearTimeout(closeT);
      stopAim();
      if (!isOpen) return;
      isOpen = false;
      item.classList.remove('is-open');
      tab.setAttribute('aria-expanded', 'false');
      panel.setAttribute('inert', '');
      release();
      if (returnFocus) tab.focus({ preventScroll: true });
    }
    item.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse' || Date.now() - lastTouch < 500) return;
      if (item.hasAttribute('data-cut')) return;                              /* a tab THE TAB RAIL hides at rest opens nothing */
      stopAim();
      wakeArt(panel);
      clearTimeout(closeT);
      openT = setTimeout(open, 90);
    });
    item.addEventListener('pointerleave', function (e) {
      /* the after-touch guard, as on the enter: WebKit fires a mouse-type pointerleave after a tap when the rail's glide
         moves the tab out from under the finger, and the panel the tap just opened shut 160 ms later */
      if (e.pointerType !== 'mouse' || Date.now() - lastTouch < 500) return;
      clearTimeout(openT);
      if (isOpen) startAim(e.clientX, e.clientY);                             /* THE SAFE TRIANGLE, or the 160 ms grace */
    });
    item.addEventListener('touchstart', function () { lastTouch = Date.now(); }, { passive: true });
    /* THE CLICK THAT KEEPS (dev2.15-fix-chrome, 30 Oct 2026; the sweep's P1-1): the hover opens the panel at 90 ms and the
       click most people make next toggled it shut (measured: a click 90 ms or more after the hover left FRAGRANCES and HELP
       closed, 1440 and 960, Chromium and WebKit). A MOUSE click now only opens (the tabs are buttons with no page of their
       own); a second click on an open tab keeps it; the mouse closes it by leaving, a press outside, Escape. A key (Enter or
       Space: a click with detail 0 and no press) and a touch or a pen keep the toggle. The press's type is read on the tab's
       own pointerdown (a click is not a PointerEvent in every engine). */
    tab.addEventListener('pointerdown', function (e) { downType = e.pointerType || ''; });
    tab.addEventListener('click', function (e) {
      var how = downType;
      downType = '';
      if (how === 'mouse' && e.detail !== 0 && Date.now() - lastTouch >= 500) { clearTimeout(openT); open(); return; }
      isOpen ? close(false) : open();
    });
    tab.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        open();
        var first = panel.querySelector('a[href]');
        /* with THE TAB RAIL the tab may glide clear of a fade first (240 ms, the panel hidden while parked): wait it out */
        if (first) setTimeout(function () { first.focus(); }, (window.RYZE.navRail && item.closest('[data-ryze-rail]')) ? 280 : 30);   /* HELP never glides (dev2.15-polish) */
      }
    });
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) { e.stopPropagation(); close(true); }
    });
    item.addEventListener('focusout', function (e) {
      if (isOpen && e.relatedTarget && !item.contains(e.relatedTarget)) close(false);
    });
    document.addEventListener('pointerdown', function (e) {
      if (isOpen && !item.contains(e.target)) close(false);
    });
    window.addEventListener('scroll', function () {
      if (isOpen && Math.abs(window.scrollY - openY) >= 8) close(false);
    }, { passive: true });
    window.addEventListener('resize', function () { if (isOpen && !deskMQ.matches) close(false); });
    panelClosers.push(function () { close(false); });
    panelOthers.push({ item: item, close: function () { close(false); } });
  });

  /* ── 5. THE SEARCH ────────────────────────────────────────── */
  var search = document.getElementById('ryze-search');
  /* CODE-26 (2026-10-18): every part the block below dereferences must be
     there; a missing one used to throw here and abort sections 6-7. */
  if (search && ['.ryze-search__input', '.ryze-search__form', '.ryze-search__results', '.ryze-search__clear',
    '.ryze-search__recent', '.ryze-search__recent-chips', '.ryze-search__recent-clear'].every(function (sel) {
    return search.querySelector(sel);
  })) {
    var input = search.querySelector('.ryze-search__input');
    var form = search.querySelector('.ryze-search__form');
    var results = search.querySelector('.ryze-search__results');
    var clearBtn = search.querySelector('.ryze-search__clear');
    var recentBox = search.querySelector('.ryze-search__recent');
    var recentChips = search.querySelector('.ryze-search__recent-chips');
    var panelEl = search.querySelector('.ryze-search__panel');
    var closeBtn = search.querySelector('.ryze-search__close');
    var cache = new Map(), ctrl = null, debounceT = 0, trigger = null, closeT2 = 0, active = -1;
    var searchOpen = false, fromDrawer = false;
    /* THE SHEET IN THE SLAB (dev2.15-search, 2026-10-30; SVNTY's svnty-headstack.js "the doors hang in the slab"; the iOS 26
       door law, ledger 30 Oct 03:37Z): iOS 26 Safari paints its status area from the STATIC element at the page's top edge and
       keeps its last sample while a NEW position: fixed layer covers that edge, so a fixed sheet at inset 0 froze the bar's
       gold over an open sheet. The sheet therefore lives inside div.ryze-strip (the sticky first child of body, the element
       Safari samples), absolute at the strip's top: the whole screen from the top on a phone, and its paper is the sample.
       html.ryze-sheet-on lifts the strip's stacking level over the two fixed layers while the sheet shows (ryze-header.css). */
    if (strip && search.parentNode !== strip) {
      strip.removeAttribute('aria-hidden');   /* the strip itself is empty paint; the dialog inside must stay in the tree */
      strip.appendChild(search);
    }
    var RKEY = 'ryze:search:recent';
    var url = (window.routes && window.routes.predictive_search_url) || '/search/suggest';

    /* CODE-25 (2026-10-18): shape-checked like the search page's reader. */
    function readRecent() {
      try {
        var parsed = JSON.parse(localStorage.getItem(RKEY) || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(function (x) { return typeof x === 'string' && x.trim().length > 0; })
          .map(function (x) { return x.trim(); }).slice(0, 6);
      } catch (e) { return []; }
    }
    function saveRecent(q) {
      q = (q || '').trim();
      if (q.length < 2) return;
      var list = readRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q);
      try { localStorage.setItem(RKEY, JSON.stringify(list.slice(0, 6))); } catch (e) {}
    }
    function renderRecent() {
      var list = readRecent();
      recentChips.textContent = '';
      list.forEach(function (q) {
        var a = document.createElement('a');
        a.className = 'ryze-chip';
        a.href = (window.routes && window.routes.search_url || '/search') + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
        a.textContent = q;
        recentChips.appendChild(a);
      });
      recentBox.hidden = list.length === 0;
    }
    search.querySelector('.ryze-search__recent-clear').addEventListener('click', function () {
      try { localStorage.removeItem(RKEY); } catch (e) {}
      renderRecent();
      input.focus();
    });

    function options() { return Array.prototype.slice.call(results.querySelectorAll('[role="option"]')); }
    function select(i) {
      var opts = options();
      opts.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
      active = i;
      if (i < 0 || !opts[i]) { input.removeAttribute('aria-activedescendant'); return; }
      opts[i].setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', opts[i].id);
      opts[i].scrollIntoView({ block: 'nearest' });
    }
    function showResults(html) {
      results.innerHTML = html;
      active = -1;
      input.removeAttribute('aria-activedescendant');
      input.setAttribute('aria-expanded', options().length ? 'true' : 'false');
    }
    function query(q) {
      if (cache.has(q)) { showResults(cache.get(q)); return; }
      if (ctrl) ctrl.abort();
      ctrl = new AbortController();
      var params = 'q=' + encodeURIComponent(q) + '&section_id=ryze-predictive-search' +
        '&resources[type]=product,query&resources[limit]=6&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last';
      fetch(url + '?' + params, { signal: ctrl.signal })
        .then(function (r) { if (!r.ok) throw new Error('suggest ' + r.status); return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var ps = doc.querySelector('[data-ryze-ps]');
          var html = ps ? ps.outerHTML : '';
          cache.set(q, html);
          if (input.value.trim() === q) showResults(html);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          showResults('<p class="ryze-ps__empty">Search is not answering right now. Press Enter to search the shop.</p>');
        });
    }
    function onInput() {
      var q = input.value.trim();
      clearBtn.hidden = input.value.length === 0;
      clearTimeout(debounceT);
      if (q.length < 2) {
        search.classList.remove('is-typing');
        input.setAttribute('aria-expanded', 'false');
        if (ctrl) ctrl.abort();
        return;
      }
      search.classList.add('is-typing');
      debounceT = setTimeout(function () { query(q); }, 250);
    }
    input.addEventListener('input', onInput);
    clearBtn.addEventListener('click', function () { input.value = ''; onInput(); input.focus(); });
    input.addEventListener('keydown', function (e) {
      var opts = options();
      if (e.key === 'ArrowDown' && opts.length) { e.preventDefault(); select((active + 1) % opts.length); }
      else if (e.key === 'ArrowUp' && opts.length) { e.preventDefault(); select(active <= 0 ? opts.length - 1 : active - 1); }
      else if (e.key === 'Enter' && active >= 0 && opts[active]) {
        e.preventDefault();
        saveRecent(input.value);
        var a = opts[active].querySelector('a');
        if (a) window.location.href = a.href;
      }
    });
    form.addEventListener('submit', function () { saveRecent(input.value); });
    results.addEventListener('click', function (e) { if (e.target.closest('a')) saveRecent(input.value); });

    /* S-4 (SVNTY's resetForClose): the sheet always reopens on its curated page; the field, the answer and the typing state
       are cleared once the close's fade has run (or at the next open, if it comes first) */
    var needsReset = false;
    function resetSheet() {
      needsReset = false;
      clearTimeout(debounceT);
      if (ctrl) { ctrl.abort(); ctrl = null; }
      input.value = '';
      input.removeAttribute('dir');
      clearBtn.hidden = true;
      search.classList.remove('is-typing');
      results.textContent = '';
      active = -1;
      input.removeAttribute('aria-activedescendant');
      input.setAttribute('aria-expanded', 'false');
      var body = search.querySelector('.ryze-search__body');
      if (body) body.scrollTop = 0;
    }
    /* THE DESK GUARD (SVNTY's: the way back exists only where the drawer does): a window that crosses into the desk row
       (750+, or a rotated iPad) while the sheet is open drops the back arrow; the close is then a plain close */
    function setFromDrawer(on) {
      fromDrawer = on;
      search.classList.toggle('is-from-drawer', on);
      if (closeBtn) closeBtn.setAttribute('aria-label', on ? 'Back to the menu' : 'Close search');
    }
    onMQ(deskMQ, function (e) { if (e.matches && fromDrawer) setFromDrawer(false); });
    function openSearch(from, viaDrawer) {
      clearTimeout(closeT2);
      if (needsReset) resetSheet();
      trigger = from || document.activeElement;
      /* THE WAY BACK (SVNTY S-10 + svnty-drawer.js cameFromDrawer): opened from the drawer's pill, the close is a back arrow
         and closing brings the drawer back; the drawer's own close runs first (one lock holder at a time, the handoff below) */
      setFromDrawer((!!viaDrawer || drawerOpen()) && !deskMQ.matches);
      if (drawerOpen()) closeDrawer(false);
      renderRecent();
      searchOpen = true;
      root.classList.add('ryze-sheet-on');
      search.hidden = false;
      /* the curated page from its top, every rail at its start (narrow refute N3: the close's reset ran on a hidden scroller,
         which keeps its offset; the sheet is shown now, so the zero holds) */
      var sheetBody = search.querySelector('.ryze-search__body');
      if (sheetBody) sheetBody.scrollTop = 0;
      search.querySelectorAll('[data-ryze-sheet-rail]').forEach(function (r) { r.scrollLeft = 0; });
      try { document.dispatchEvent(new CustomEvent('ryze:sheet:open')); } catch (e) {}
      nav.classList.add('search-active'); /* ryze-promo.js reads this: no present over a query */
      document.querySelectorAll('[data-ryze-search-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      lock();
      /* THE FOCUS LAW (SVNTY's search sheet, assets/svnty-search.js; the owner, 30 Oct 2026 05:4x Cairo: "like in SVNTY do
         not make the search bar focused"): the close on touch, the field on a mouse. On a phone the field's focus raised the
         keyboard with the sheet, and iOS 26 kept the bar's gold under its status area through the keyboard's rise (the park
         had landed: html.ryze-parked, the strip clear, the curtain paper; the knocks at 700 / 1600 ms fell inside the
         keyboard's animation and Safari never re-sampled). The close button takes the focus instead (the trap's seat), so
         the sheet opens quiet, the shopper taps the field when they want to type, and the strip reads paper. */
      if (COARSE) {
        input.blur();
        /* the sheet itself holds the focus (tabindex -1, no ring: ryze-header.css), not the close button: WebKit paints
           a focus ring on a programmatically focused button (the owner saw the X highlighted, 30 Oct 05:5x Cairo) */
        panelEl.setAttribute('tabindex', '-1');
        panelEl.focus({ preventScroll: true });
      } else {
        input.focus({ preventScroll: true });
      }
      requestAnimationFrame(function () { search.classList.add('is-open'); });
    }
    function closeSearch(goBack) {
      if (!searchOpen) return;
      searchOpen = false;
      var back = goBack !== false && fromDrawer && !deskMQ.matches && !!drawer;   /* P2-1: never reopen a drawer the width has hidden */
      fromDrawer = false;
      needsReset = true;
      search.classList.remove('is-open');
      nav.classList.remove('search-active');
      document.querySelectorAll('[data-ryze-search-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      unlock();
      closeT2 = setTimeout(function () {
        search.hidden = true;
        search.classList.remove('is-from-drawer');
        root.classList.remove('ryze-sheet-on');
        if (needsReset) resetSheet();
        try { document.dispatchEvent(new CustomEvent('ryze:sheet:closed')); } catch (e) {}
      }, reduceMQ.matches ? 0 : 240);
      /* THE WAY BACK: the drawer takes the lock again in the same task as the release, so the bar never bounces between the
         two doors (the park stays, the knocks run once more). A keyboard or a mouse lands on the drawer's first control
         (SVNTY S-6: the door that opens takes the focus back), never on <body> behind the modal; touch focuses nothing. */
      if (back) {
        openDrawer();
        if (!COARSE) focusDrawerFirst();
        return;
      }
      /* THE FOCUS LAW on the close: a keyboard gets its trigger back; on touch nothing takes the focus (WebKit would paint
         the icon's ring after a tap, the owner's "double outline") */
      if (COARSE) { if (trigger && trigger.blur) trigger.blur(); }
      else if (trigger && trigger.focus && trigger.offsetParent !== null) trigger.focus({ preventScroll: true });
    }
    closeSearchRef = function () { closeSearch(false); };   /* the drawer opening over a sheet: no way back to itself */
    document.querySelectorAll('[data-ryze-search-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
      var inDrawer = !!b.closest('#ryze-mobile-menu');
      b.addEventListener('click', function () {
        if (searchOpen && !inDrawer) { closeSearch(); return; }   /* a keyboard press on the header icon under a desk panel */
        openSearch(inDrawer ? burger : b, inDrawer);
      });
    });
    search.querySelectorAll('[data-ryze-search-close]').forEach(function (b) { b.addEventListener('click', function () { closeSearch(); }); });
    search.addEventListener('keydown', function (e) {
      /* P2-2: Escape is one step: from the drawer it goes back to the drawer (the arrow's own promise) and stops here, so
         the drawer's document listener never reads the same key as its own close */
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeSearch(); return; }
      trapTab(e, [panelEl]);
    });
    /* THE EDITOR GUARD: a header re-render in the theme editor prints a second, unwired #ryze-search into the section; the
       wired sheet (moved into the strip at load) stays the one, the copy leaves (a setting change previews after a reload) */
    document.addEventListener('shopify:section:load', function (e) {
      var dup = e.target && e.target.querySelector ? e.target.querySelector('#ryze-search') : null;
      if (dup && dup !== search && dup.parentNode) dup.parentNode.removeChild(dup);
    });
    window.RYZE.openSearch = openSearch;
    window.RYZE.closeSearch = closeSearch;
  }

  /* ── 6. THE ROLLER ────────────────────────────────────────── */
  if (bar && bar.hasAttribute('data-ryze-roller')) {
    var phoneMQ = window.matchMedia('(max-width: 749px)');
    var pauseBtn = bar.querySelector('.ryze-announce__pause');
    var live = bar.querySelector('[data-ryze-roller-live]');
    var holds = new Set(), idx = 0, holdT = 0, set = [];
    function pick() {
      set = Array.prototype.slice.call(bar.querySelectorAll(phoneMQ.matches ? '.ryze-announce__msg--narrow' : '.ryze-announce__msg--wide'));
      bar.querySelectorAll('.ryze-announce__msg').forEach(function (m) { m.classList.remove('is-current', 'is-leaving'); m.setAttribute('aria-hidden', 'true'); });
      idx = 0;
      if (set[0]) { set[0].classList.add('is-current'); set[0].removeAttribute('aria-hidden'); }
      if (pauseBtn) pauseBtn.hidden = set.length < 2;
    }
    function step(delta, byUser) {
      if (set.length < 2) return;
      var cur = set[idx];
      idx = (idx + delta + set.length) % set.length;
      var next = set[idx];
      bar.setAttribute('data-dir', delta < 0 ? 'back' : 'fwd');
      set.forEach(function (m) { m.classList.remove('is-leaving'); });
      cur.classList.remove('is-current');
      cur.classList.add('is-leaving');
      cur.setAttribute('aria-hidden', 'true');
      next.classList.add('is-current');
      next.removeAttribute('aria-hidden');
      if (byUser && live) { live.setAttribute('aria-live', 'polite'); live.textContent = next.textContent; }
      schedule();
    }
    function schedule() {
      clearTimeout(holdT);
      if (holds.size || set.length < 2) return;
      holdT = setTimeout(function () { step(1, false); }, 4000);
    }
    function hold(reason, on) { if (on) holds.add(reason); else holds.delete(reason); schedule(); }
    bar.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hold('hover', true); });
    bar.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hold('hover', false); });
    bar.addEventListener('focusin', function () { hold('focus', true); });
    bar.addEventListener('focusout', function () { hold('focus', false); });
    var sx = 0, sy = 0;
    bar.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; hold('touch', true); }, { passive: true });
    bar.addEventListener('touchend', function (e) {
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      if (Math.abs(dx) >= 24 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1, true);
      hold('touch', false);
    }, { passive: true });
    bar.addEventListener('touchcancel', function () { hold('touch', false); }, { passive: true });
    bar.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1, true); }
    });
    document.addEventListener('visibilitychange', function () { hold('hidden', document.hidden); });
    if (pauseBtn) pauseBtn.addEventListener('click', function () {
      var paused = pauseBtn.getAttribute('aria-pressed') !== 'true';
      pauseBtn.setAttribute('aria-pressed', String(paused));
      pauseBtn.setAttribute('aria-label', paused ? 'Play the announcements' : 'Pause the announcements');
      hold('paused', paused);
    });
    onMQ(phoneMQ, function () { pick(); schedule(); });
    pick();
    schedule();
  }

  /* ── 7. THE CART LABEL ────────────────────────────────────── */
  var cartLink = nav.querySelector('[data-ryze-cart-link]');
  var cartCount = cartLink && cartLink.querySelector('.ryze-nav__cart-count');
  if (cartCount) {
    var label = function () {
      var n = parseInt(cartCount.textContent, 10) || 0;
      if (cartCount.style.display === 'none') n = 0;
      cartLink.setAttribute('aria-label', 'Cart, ' + n + (n === 1 ? ' item' : ' items'));
    };
    new MutationObserver(label).observe(cartCount, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    label();
  }
})();
